/**
 * API Configuration for SurvAI Frontend
 * 
 * This module handles all API communication with the backend server.
 * It supports both session-based authentication and JWT tokens (PKCE flow).
 */

// Get API URL from environment variable or default to relative path (for proxy in dev)
export const API_URL = import.meta.env.VITE_API_URL || "";

// Token storage key
const TOKEN_KEY = "survai_auth_token";

// Token management
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Build full API URL
function buildUrl(path: string): string {
  // If API_URL is empty (using proxy), just use the path
  if (!API_URL) {
    return path;
  }
  // Ensure no double slashes
  return `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

// Get auth headers
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// Throw error if response is not ok
async function throwIfResNotOk(res: Response): Promise<void> {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Make an API request to the backend
 */
export async function apiRequest(
  method: string,
  path: string,
  data?: unknown | undefined,
): Promise<Response> {
  const url = buildUrl(path);
  const isFormData = data instanceof FormData;
  
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
  };
  
  if (!isFormData && data) {
    headers["Content-Type"] = "application/json";
  }
  
  const res = await fetch(url, {
    method,
    headers,
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

// Type for unauthorized behavior
type UnauthorizedBehavior = "returnNull" | "throw";

/**
 * Create a query function for React Query
 */
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => (context: { queryKey: readonly unknown[] }) => Promise<T | null> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const path = queryKey[0] as string;
    const url = buildUrl(path);
    
    const res = await fetch(url, {
      credentials: "include",
      headers: getAuthHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * Auth-specific API functions
 */
export const authApi = {
  async login(email: string, password: string) {
    const res = await apiRequest("POST", "/api/auth/login", { email, password });
    const data = await res.json();
    if (data.token) {
      setAuthToken(data.token);
    }
    return data;
  },

  async register(email: string, password: string, firstName: string, lastName: string) {
    const res = await apiRequest("POST", "/api/auth/register", { email, password, firstName, lastName });
    const data = await res.json();
    if (data.token) {
      setAuthToken(data.token);
    }
    return data;
  },

  async logout() {
    await apiRequest("POST", "/api/auth/logout");
    removeAuthToken();
  },

  async getCurrentUser() {
    try {
      const res = await apiRequest("GET", "/api/auth/user");
      return await res.json();
    } catch {
      return null;
    }
  },

  async forgotPassword(email: string) {
    const res = await apiRequest("POST", "/api/auth/forgot-password", { email });
    return await res.json();
  },

  // Handle OAuth callback (PKCE flow)
  handleOAuthCallback(token: string) {
    setAuthToken(token);
  },

  // Get Google OAuth URL
  getGoogleOAuthUrl() {
    return buildUrl("/api/auth/google");
  },
};

/**
 * Check if error is unauthorized
 */
export function isUnauthorizedError(error: Error): boolean {
  return /^401: .*Unauthorized/.test(error.message) || error.message.includes("401");
}

